import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  PageHeader,
  Table,
  Input,
  Avatar,
  Tag,
  Modal,
  message,
  Typography,
  Alert,
  Card,
  Space,
  Switch,
  Divider,
  Button,

} from 'antd';

import {
  SearchOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { getAllMembers, getPlanInfo, toggleMemberActive } from './workspaceMembers.action';

const { confirm } = Modal;
const { Text } = Typography;

class WorkspaceMembers extends Component {
  constructor(props) {
    super();
    this.state = {
      search: '',
      searchTimeout: null,
      billingData: null,
      page: 1,
    };
  }

  componentDidMount() {
    this.fetchAll();
    const { wId } = this.props.match.params;
    this.props.getPlanInfo(wId);
  }

  fetchAll = (page = this.state.page, search = this.state.search) => {
    const { wId } = this.props.match.params;
    if (page !== this.state.page) this.setState({ page });
    this.props.getAllMembers(wId, { page, limit: 10, search });
  };

  onSearchChange = (e) => {
    const search = e.target.value;
    clearTimeout(this.state.searchTimeout);
    const searchTimeout = setTimeout(() => this.fetchAll(1, search), 400);
    this.setState({ search, searchTimeout });
  };

  handleToggle = async (wmId, makeActive) => {
    const { wId } = this.props.match.params;
    try {
      const res = await this.props.toggleMemberActive(wId, wmId, makeActive);
      const data = res && res.data;
      if (data && data.errors === 'change_plan') {
        message.warning(
          `Plan limit reached. You can activate ${data.usersLimitAvailable ?? 0} more member${data.usersLimitAvailable !== 1 ? 's' : ''} on your current plan. Upgrade to activate more.`
        );
        return;
      }
      message.success(makeActive ? 'Member activated' : 'Member deactivated');
      this.fetchAll();
      this.props.getPlanInfo(wId);
    } catch {
      message.error('Something went wrong');
    }
  };

  getMemberName = (record) =>
    (record.slackInfo && record.slackInfo.displayName) ||
    (record.user_id && (record.user_id.displayName || record.user_id.name)) ||
    record.email ||
    '';

  confirmDeactivate = (record) => {
    const { user, isAdmin } = this.props;

    if (!isAdmin) {
      message.error('Only admins can deactivate members.');
      return;
    }

    const recordUserId = record.user_id && (record.user_id._id || record.user_id);
    const currentUserId = user && (user._id || user.id);
    if (recordUserId && currentUserId && String(recordUserId) === String(currentUserId)) {
      message.error('You cannot deactivate yourself.');
      return;
    }

    const name = this.getMemberName(record);

    if (record.role === 'admin') {
      confirm({
        title: `${name} is an admin`,
        icon: <ExclamationCircleOutlined />,
        content: 'Please remove them from admin role before deactivating.',
        okText: 'OK',
        cancelButtonProps: { style: { display: 'none' } },
      });
      return;
    }

    confirm({
      title: `Deactivate ${name}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This member will lose access to the workspace. You can reactivate them any time.',
      okText: 'Deactivate',
      cancelText: 'Cancel',
      onOk: () => this.handleToggle(record._id, false),
    });
  };

  confirmActivate = (record) => {
    const name = this.getMemberName(record);
    confirm({
      title: `Activate ${name}?`,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      content: 'This member will regain access to the workspace.',
      okText: 'Activate',
      cancelText: 'Cancel',
      onOk: () => this.handleToggle(record._id, true),
    });
  };

  getColumns = () => [
    {
      title: 'Member',
      key: 'member',
      render: (_, record) => {
        const displayName = this.getMemberName(record);
        const profilePic = record.slackInfo && record.slackInfo.profilePicUrl;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar src={profilePic}>
              {displayName ? displayName[0].toUpperCase() : '?'}
            </Avatar>
            <span>{displayName}</span>
          </div>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'admin' ? 'purple' : 'default'}>
          {role === 'admin' ? 'Administrator' : 'Member'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      key: 'is_active',
      render: (_, record) => (
        <Switch
          checked={record.is_active}
          checkedChildren='Active'
          unCheckedChildren='Inactive'
          onChange={(checked) =>
            checked ? this.confirmActivate(record) : this.confirmDeactivate(record)
          }
        />
      ),
    },
  ];

  getPlanLabel = () => {
    const { planInfo, workspace } = this.props;
    if (workspace && workspace.is_enterprise) return 'Enterprise';
    if (planInfo && planInfo.plan_status === 'free') return 'Free Plan';
    if (workspace && workspace.billing_status === 'trial') return 'Trial';
    if (workspace && workspace.billing_status === 'free') return 'Free Plan';
    return 'Pro Plan';
  };



  render() {
    const { allMembers, allPagination, planInfo, loading } = this.props;
    const isLimitBased = planInfo?.billing_type === 'limit_based';
    const { search } = this.state;

    const allowedUsers = planInfo && planInfo.limits && planInfo.limits.allowedActiveUsers;
    const currentActive = (planInfo && planInfo.limits && planInfo.limits.currentActiveUsers) || 0;
    const planLabel = this.getPlanLabel();
    const limitExceeded = allowedUsers && currentActive === allowedUsers;
    const isFreeTrial = planLabel === 'Free Plan' || planLabel === 'Trial';
    const showUpgrade = isFreeTrial || limitExceeded;
    const { wId } = this.props.match.params;

    return (
      <div className='flex-col' style={{ width: '100%', overflow: 'hidden' }}>
        <PageHeader title='People' />


        {isLimitBased && <>
          <div style={{ padding: '12px 32px 0', marginBottom: 32 }}>
            <Card size='small' style={{ borderRadius: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space split={<Divider type='vertical' />}>
                  <Space size={6}>
                    <CrownOutlined style={{ color: '#722ed1' }} />
                    <Text strong>{planLabel}</Text>
                  </Space>
                  <Space size={6}>
                    <TeamOutlined />
                    <Text>
                      <Text strong>{currentActive}</Text>
                      {allowedUsers ? (
                        <> of <Text strong>{allowedUsers}</Text> active user slots used</>
                      ) : (
                        ' active users'
                      )}
                    </Text>
                  </Space>
                  {limitExceeded && (
                    <Text type='warning'>Limit reached — deactivate a member or upgrade to add more</Text>
                  )}

                </Space>

                {showUpgrade && (
                  <Button
                    type='primary'
                    size='small'
                    onClick={() => this.props.history.push(`/${wId}/settings?view=upgrade`)}
                  >
                    Upgrade Plan
                  </Button>
                )}
              </div>
            </Card>
          </div>
          <Alert
            banner
            type='info'
            showIcon
            message={
              <span>
                Everyone from your Slack workspace is listed below. Toggle someone to Active to let them use Troopr.
              </span>
            }
            style={{ margin: "0 32px" }}
          /></>}
        <div style={{ padding: '16px 32px', height: 'calc(100vh - 200px)', overflow: 'auto', boxSizing: 'border-box' }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder='Search people'
            value={search}
            onChange={this.onSearchChange}
            style={{ marginBottom: 16, maxWidth: 400 }}
            allowClear
          />
          <Table
            rowKey='_id'
            size='small'
            dataSource={allMembers}
            columns={this.getColumns()}
            loading={loading}
            onChange={(p) => this.fetchAll(p.current, search)}
            style={{ width: '100%' }}
            pagination={{
              current: allPagination && allPagination.page,
              total: allPagination && allPagination.total,
              pageSize: allPagination && allPagination.limit,
              showTotal: (t) => `${t} people`,
              showSizeChanger: false,
            }}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  allMembers: state.workspaceMembers.allMembers,
  allPagination: state.workspaceMembers.allPagination,
  planInfo: state.workspaceMembers.planInfo,
  loading: state.workspaceMembers.loading,
  user: state.common_reducer.user,
  isAdmin: state.common_reducer.isAdmin,
  workspace: state.common_reducer.workspace,
});

export default withRouter(
  connect(mapStateToProps, { getAllMembers, getPlanInfo, toggleMemberActive })(WorkspaceMembers)
);
